// Editor de textos sobre la propia web (solo administración).
// La administradora pulsa «Editar textos», hace clic en cualquier título o párrafo,
// lo reescribe y al guardar se traduce solo a los otros 3 idiomas.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pencil, X, Loader2, Check } from 'lucide-react';
import i18n from '../i18n/i18n';
import { useAuth } from '../context/AuthContext';
import { ET } from '../lib/empresasTxt';
import { aplicarTextos } from '../i18n/overrides';
import { traducirTexto, putContenidoPortada } from '../lib/adminApi';

const LANGS = ['es', 'en', 'ru', 'fr'];
const NOMBRE = { es: 'español', en: 'inglés', ru: 'ruso', fr: 'francés' };
const norm = (s) => String(s || '').replace(/\s+/g, ' ').trim();

function aplanar(obj, pref = '', out = {}) {
  for (const [k, v] of Object.entries(obj || {})) {
    const c = pref ? `${pref}.${k}` : k;
    if (v && typeof v === 'object') aplanar(v, c, out);
    else if (typeof v === 'string') out[c] = v;
  }
  return out;
}

function escapar(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

// Lista de textos del idioma actual (con los cambios ya guardados) para buscar por contenido.
function indice(lang) {
  const base = aplanar(i18n.getResourceBundle(lang, 'translation') || {});
  for (const [k, v] of Object.entries(ET[lang] || {})) if (typeof v === 'string') base[`empresas_txt.${k}`] = v;
  const out = [];
  for (const [clave, valor] of Object.entries(base)) {
    if (clave.startsWith('admin.') || !/^[A-Za-z0-9_.-]{1,150}$/.test(clave)) continue;
    const v = norm(valor);
    if (!v) continue;
    const tieneVars = /\{\{.+?\}\}/.test(v);
    out.push({ clave, valor, v, rx: tieneVars ? new RegExp(`^${escapar(v).replace(/\\\{\\\{.+?\\\}\\\}/g, '.+?')}$`) : null });
  }
  return out;
}

function buscar(idx, el) {
  let nodo = el;
  for (let i = 0; i < 5 && nodo && nodo !== document.body; i += 1, nodo = nodo.parentElement) {
    const txt = norm(nodo.textContent);
    if (!txt || txt.length > 700) continue;
    const claves = idx.filter((e) => e.v === txt || (e.rx && e.rx.test(txt)));
    if (claves.length) return { nodo, claves };
  }
  return null;
}

export default function EditorEnVivo() {
  const { user } = useAuth();
  const esAdmin = user && user.rol === 'admin';
  const [activo, setActivo] = useState(false);
  const [sel, setSel] = useState(null);      // { claves, lang, texto }
  const [texto, setTexto] = useState('');
  const [estado, setEstado] = useState('idle'); // idle | guardando | ok | error
  const [aviso, setAviso] = useState('');
  const [msg, setMsg] = useState('');
  const hover = useRef(null);
  const idxRef = useRef(null);

  const limpiarHover = useCallback(() => { if (hover.current) { hover.current.classList.remove('ev-hover'); hover.current = null; } }, []);

  const alClick = useCallback((e) => {
    if (e.target.closest('.ev-ui')) return;
    const lang = i18n.resolvedLanguage || 'es';
    idxRef.current = indice(lang);
    const r = buscar(idxRef.current, e.target);
    e.preventDefault(); e.stopPropagation();
    if (!r) { setMsg('Este texto no se edita aquí (viene de otra sección, como las clases, los bonos o las profesoras). Edítalo desde su apartado del panel.'); return; }
    setMsg(''); limpiarHover();
    setSel({ claves: r.claves.map((c) => c.clave), lang, valor: r.claves[0].valor });
    setTexto(r.claves[0].valor); setEstado('idle'); setAviso('');
  }, [limpiarHover]);

  const alMover = useCallback((e) => {
    if (e.target.closest('.ev-ui')) { limpiarHover(); return; }
    const lang = i18n.resolvedLanguage || 'es';
    if (!idxRef.current || idxRef.current.lang !== lang) { idxRef.current = indice(lang); idxRef.current.lang = lang; }
    const r = buscar(idxRef.current, e.target);
    const nodo = r ? r.nodo : null;
    if (nodo === hover.current) return;
    limpiarHover();
    if (nodo) { nodo.classList.add('ev-hover'); hover.current = nodo; }
  }, [limpiarHover]);

  useEffect(() => {
    if (!activo) { limpiarHover(); return undefined; }
    document.addEventListener('click', alClick, true);
    document.addEventListener('mouseover', alMover, true);
    document.body.classList.add('ev-editando');
    return () => {
      document.removeEventListener('click', alClick, true);
      document.removeEventListener('mouseover', alMover, true);
      document.body.classList.remove('ev-editando');
      limpiarHover();
    };
  }, [activo, alClick, alMover, limpiarHover]);

  const otros = useMemo(() => (sel ? LANGS.filter((l) => l !== sel.lang) : []), [sel]);

  const guardar = async () => {
    const nuevo = texto.trim();
    if (!nuevo || !sel) return;
    setEstado('guardando'); setAviso('');
    let textos = { [sel.lang]: nuevo };
    try {
      const r = await traducirTexto(nuevo, sel.lang);
      textos = { ...textos, ...r.textos };
    } catch (e) {
      setAviso(`No se pudo traducir automáticamente: el texto se guarda solo en ${NOMBRE[sel.lang]}.`);
    }
    try {
      const envio = {};
      for (const c of sel.claves) envio[c] = textos;
      await putContenidoPortada(envio);
      aplicarTextos(Object.fromEntries(LANGS.filter((l) => textos[l]).map((l) => [l, Object.fromEntries(sel.claves.map((c) => [c, textos[l]]))])));
      idxRef.current = null;
      setEstado('ok');
      setTimeout(() => setSel(null), 900);
    } catch (e) {
      setEstado('error'); setAviso(e.message || 'No se pudo guardar. Inténtalo de nuevo.');
    }
  };

  if (!esAdmin) return null;
  return createPortal(
    <div className="ev-ui">
      {!activo && (
        <button type="button" className="ev-boton" onClick={() => setActivo(true)}>
          <Pencil size={16} /> Editar textos
        </button>
      )}
      {activo && (
        <div className="ev-barra">
          <span>Modo edición: pulsa cualquier título o texto de la web para cambiarlo.</span>
          <button type="button" onClick={() => { setActivo(false); setSel(null); setMsg(''); }}><X size={15} /> Terminar</button>
        </div>
      )}
      {activo && msg && <div className="ev-msg" onClick={() => setMsg('')}>{msg}</div>}
      {sel && (
        <div className="ev-fondo" role="dialog" aria-modal="true">
          <div className="ev-modal">
            <h3>Cambiar este texto</h3>
            <textarea rows={Math.min(8, Math.max(3, Math.ceil(texto.length / 50)))} value={texto} onChange={(e) => { setTexto(e.target.value); setEstado('idle'); }} autoFocus />
            {/\{\{.+?\}\}/.test(sel.valor) && <p className="ev-nota">Las palabras entre dobles llaves, como {'{{n}}'}, se sustituyen solas: déjalas tal cual.</p>}
            {sel.claves.length > 1 && <p className="ev-nota">Este mismo texto aparece en {sel.claves.length} sitios de la web; se cambiará en todos.</p>}
            <p className="ev-nota">Al guardar se traducirá automáticamente a: {otros.map((l) => NOMBRE[l]).join(', ')}.</p>
            {aviso && <p className="ev-aviso">{aviso}</p>}
            <div className="ev-acciones">
              <button type="button" className="ev-sec" onClick={() => setSel(null)} disabled={estado === 'guardando'}>Cancelar</button>
              <button type="button" className="ev-pri" onClick={guardar} disabled={estado === 'guardando' || !texto.trim() || texto.trim() === sel.valor}>
                {estado === 'guardando' ? <><Loader2 size={15} className="spin" /> Traduciendo y guardando…</> : estado === 'ok' ? <><Check size={15} /> Guardado</> : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
